import MissionAdGeneratorModal from './MissionAdGeneratorModal';
import MissionDetailPanel from './MissionDetailPanel';
import { useClients } from '../../hooks/useClients';
import MissionMatchResults from './MissionMatchResults';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Plus, MapPin, Calendar, Users, ArrowUpRight, Clock, Euro, AlertOctagon, ChevronRight, Search as SearchIcon, Copy, Check, Zap, Briefcase, User, Brain, Hammer, Heart, FileText, Lock, MessageSquare, Target, ChevronDown, PlayCircle, PauseCircle, StopCircle, Info, XCircle, Settings, FileCode, RefreshCw, Upload, X, Sparkles, CheckCircle2, AlertTriangle, ShieldAlert, Mail, ExternalLink, Download, Share2, Phone, Linkedin, HelpCircle, MoreHorizontal, Layout, LayoutGrid, List, FileType, Trash2, ArrowRightCircle, Archive, Code, PenTool, AlertCircle, Loader2, Columns2, Building2 } from 'lucide-react';
import {
    DndContext,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    type DragStartEvent,
    type DragOverEvent,
    type DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove
} from '@dnd-kit/sortable';
import { useMissions, type PortfolioScope } from '../../hooks/useMissions';
import { useKanbanColumns, COLOR_STYLES } from '../../hooks/useKanbanColumns';
import ScopeToggle from '../shared/ScopeToggle';
import AssignDropdown from '../shared/AssignDropdown';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import type { MissionDetails, GeneratedAdContent, TalentProfile } from '../../types';
import { useNotifications } from '../../context/NotificationContext';
import { supabase } from '../../lib/supabase';
import { notifyQuotaChanged, openUpgradeModal, parseQuotaExceeded, quotaExceededMessage } from '../../lib/quotaErrors';
import FileDropZone from '../shared/FileDropZone';
import { SortableMissionCard } from './SortableMissionCard';
import { KanbanColumn } from './KanbanColumn';
import { PipelineKanbanView } from '../pipeline/PipelineKanbanView';
import MissionProgressWidget from '../analysis/MissionProgressWidget';
import { useProcessing } from '../../context/ProcessingContext';
import { useOrganization } from '../../hooks/useOrganization';
import { useAuth } from '../../context/AuthContext';

interface MissionUploadTracker {
    id: string;
    title: string;
    status: string;
    dbId: string | null;
    isClosing: boolean;
}

// Type for matching candidates (populated by AI analysis backend)
interface MatchCandidate {
    id: string;
    name: string;
    role: string;
    email: string;
    phone: string;
    location: string;
    linkedin: string;
    score: number;
    initials: string;
    skills: { name: string; type: string }[];
    summary: string;
    criteria: { label: string; status: string; proof: string }[];
    verifications: string[];
    interviewQuestions: string[];
}

// Type for kanban board items
interface KanbanItem {
    id: string;
    client: string;
    client_id: string | null;
    contact_id: string | null;
    contact_name: string | null;
    title: string;
    tjm: string;
    location: string;
    status: string;
    matchScore: number;
    lastUpdate: string;
}

// --- Build ad content from mission data ---
const buildGeneratedContent = (mission: MissionDetails): GeneratedAdContent => {
    const techSkills = mission.requiredSkills?.technical ?? [];
    const stack = techSkills.map(s => s.name).join(', ') || 'N/C';
    const city = mission.location?.split(',')[0]?.trim() || 'France';

    // Prefer AI-generated hashtags, fallback to derived
    const hashtags = mission.suggested_hashtags?.length
        ? mission.suggested_hashtags
        : [
            '#Recrutement',
            `#${city.replace(/\s+/g, '')}`,
            '#Tech',
            ...techSkills.slice(0, 4).map(s => `#${s.name.replace(/[^a-zA-Z0-9]/g, '')}`),
        ].filter(Boolean);

    // Hook: prefer AI-generated linkedin_hook
    const hook = mission.linkedin_hook
        || mission.deal_summary_one_line
        || `🚀 ${mission.title} — ${mission.client} (${city})`;

    // Full LinkedIn post: prefer AI-generated, fallback to derived
    const linkedinPost = mission.linkedin_post || [
        `🎯 ${mission.standardized_title || mission.title} — ${city}`,
        '',
        mission.missionSummary || '',
        '',
        stack ? `🛠️ Stack technique : ${stack}` : '',
        '',
        mission.pitch?.candidate ? `🎯 Profil recherché :\n${mission.pitch.candidate}` : '',
        '',
        `📩 Intéressé(e) ? Contactez-nous dès maintenant !`,
        '',
        hashtags.join(' '),
    ].filter(line => line !== undefined).join('\n');

    // Job board fields: prefer AI-generated
    const jbTitle = mission.job_board_ad?.title || mission.standardized_title || mission.title;
    const jbIntro = mission.job_board_ad?.intro || mission.missionSummary || '';
    const jbTasks = mission.job_board_ad?.tasks?.length
        ? mission.job_board_ad.tasks
        : (mission.kitDetails?.technicalQuestions?.length
            ? mission.kitDetails.technicalQuestions.map(q =>
                typeof q === 'object' && q !== null ? (q as any).question : q as string
              )
            : techSkills.map(s => `Maîtrise de ${s.name}`));
    const jbTechReq = mission.job_board_ad?.technical_requirements || stack;
    const jbSoftSkills = mission.job_board_ad?.soft_skills_mindset || '';
    const jbProfile = mission.persona || mission.pitch?.candidate || '';

    return {
        ad_hook: hook,
        linkedin_post: linkedinPost,
        job_board_description: {
            title: jbTitle,
            intro: jbIntro,
            tasks: jbTasks,
            stack,
            technical_requirements: jbTechReq,
            soft_skills_mindset: jbSoftSkills,
            profile_required: jbProfile,
        },
        suggested_hashtags: hashtags,
        outreach_message_short: mission.outreach_message_short || '',
        outreach_message_full: mission.outreach_message_full || mission.outreach_candidate_message || mission.linkedin_dm || '',
    };
};

// Helper: map DB candidate_status to icon status
const mapCandidateStatus = (s: string): string => {
    if (s === 'VALIDÉ') return 'met';
    if (s === 'PARTIEL') return 'partial';
    return 'missing';
};


const MissionRegistryView: React.FC = () => {
    const { notify } = useNotifications();
    const { id: urlMissionId, tab: urlTab } = useParams<{ id?: string; tab?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { columns: rawColumns } = useKanbanColumns();
    const [viewMode, setViewMode] = useState<'list' | 'detail' | 'create'>('list');
    const [selectedMission, setSelectedMission] = useState<MissionDetails | null>(null);
    const [copiedSection, setCopiedSection] = useState<string | null>(null);

    // Status Dropdown State for Detail View
    const [isStatusOpen, setIsStatusOpen] = useState(false);

    // Tabbed Navigation State — synced with URL param
    const [activeTab, setActiveTab] = useState<'brief' | 'talents' | 'source' | 'pipeline'>('brief');
    useEffect(() => {
        if (urlTab === 'talents' || urlTab === 'source' || urlTab === 'pipeline') setActiveTab(urlTab);
        else setActiveTab('brief');
    }, [urlTab]);
    const [currentStep, setCurrentStep] = useState(0);
    const [activeQuestionTab, setActiveQuestionTab] = useState<'technical' | 'contractual'>('technical');

    // Sync currentStep from mission status when detail view opens
    useEffect(() => {
        if (selectedMission) {
            const statusToStep: Record<string, number> = {};
            rawColumns.forEach(col => { statusToStep[col.slug] = col.position; });
            // Legacy / alternate spellings always map to first column or their canonical column
            statusToStep['prospection'] = 0;
            statusToStep['Ouvert'] = 0;
            statusToStep['Brouillon'] = 0;
            statusToStep['En Pause'] = 0;
            statusToStep['Négociation'] = statusToStep['Negociation'] ?? 3;
            statusToStep['Clôture'] = statusToStep['Cloture'] ?? 4;
            statusToStep['Fermé'] = statusToStep['Cloture'] ?? 4;
            setCurrentStep(statusToStep[selectedMission.status] ?? 0);
        }
    }, [selectedMission?.id, rawColumns]);
    const [isAdModalOpen, setIsAdModalOpen] = useState(false);


    // Matching Accordion State
    const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);

    // Drawer State
    const [drawerCandidate, setDrawerCandidate] = useState<MatchCandidate | null>(null);

    // Contact Modal State
    const [contactCandidate, setContactCandidate] = useState<MatchCandidate | null>(null);

    // Matching results — fetched from Supabase matches table
    const [matchAnalysis, setMatchAnalysis] = useState<MatchCandidate[]>([]);
    const [isLoadingMatches, setIsLoadingMatches] = useState(false);
    const [matchDate, setMatchDate] = useState<string | null>(null);

    useEffect(() => {
        if (!selectedMission) return;
        const fetchMatches = async () => {
            setIsLoadingMatches(true);
            setMatchAnalysis([]);
            setMatchDate(null);
            try {
                const { data: matches, error } = await supabase
                    .from('matches')
                    .select('*')
                    .eq('mission_id', Number(selectedMission.id))
                    .order('match_score', { ascending: false });
                if (error) throw error;
                if (!matches || matches.length === 0) return;

                setMatchDate((matches[0] as any).created_at);

                const candidateIds = matches.map((m: any) => m.candidate_id);
                const { data: candidates, error: candError } = await supabase
                    .from('candidates')
                    .select('id, full_name, summary_json')
                    .in('id', candidateIds);
                if (candError) throw candError;

                const candMap = new Map((candidates || []).map((c: any) => [c.id, c]));

                const mapped: MatchCandidate[] = matches.map((m: any) => {
                    const cand = candMap.get(m.candidate_id);
                    const sj = cand?.summary_json || {};
                    const analysis = m.match_analysis_json || {};

                    const nameParts = (cand?.full_name || '??').split(' ');
                    const initials = nameParts.length >= 2
                        ? nameParts[0][0] + nameParts[1][0]
                        : (cand?.full_name || '??').substring(0, 2);

                    const techSkills = (JSON.parse(sj.Technical_competences_keys || '[]') as any[]).slice(0, 3).map((s: any) => ({ name: s.name, type: 'tech' }));
                    const methodSkills = (JSON.parse(sj.Methods_competences_keys || '[]') as any[]).slice(0, 2).map((s: any) => ({ name: s.name, type: 'method' }));

                    const criteria = (analysis.criteria_analysis || []).map((c: any) => ({
                        label: c.criterion_name,
                        status: mapCandidateStatus(c.candidate_status),
                        proof: c.comment,
                    }));

                    const ci = sj.contactInfo || {};
                    return {
                        id: m.id.toString(),
                        name: cand?.full_name || 'Inconnu',
                        role: sj.Suggested_title || sj['profil _summary'] || '',
                        email: ci.email || '',
                        phone: ci.phone || '',
                        location: ci.location_city || '',
                        linkedin: ci.linkedin_url || '',
                        score: m.match_score,
                        initials: initials.toUpperCase(),
                        skills: [...techSkills, ...methodSkills],
                        summary: analysis.verdict_short || '',
                        criteria,
                        verifications: analysis.verification_questions || [],
                        interviewQuestions: [],
                    };
                });
                setMatchAnalysis(mapped);
            } catch (err: any) {
                console.error('Error fetching matches:', err);
            } finally {
                setIsLoadingMatches(false);
            }
        };
        fetchMatches();
    }, [selectedMission?.id]);

    // Toast State
    const [toast, setToast] = useState<{ message: string, visible: boolean }>({ message: '', visible: false });

    // Source Editor State
    const [editorContent, setEditorContent] = useState('');
    const [sourceViewMode, setSourceViewMode] = useState<'editor' | 'json'>('editor');
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [importFiles, setImportFiles] = useState<File[]>([]);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [showRegenConfirm, setShowRegenConfirm] = useState(false);
    const [showArchived, setShowArchived] = useState(false);

    // NEW MISSION MODAL STATES
    const [isNewMissionModalOpen, setIsNewMissionModalOpen] = useState(false);

    // Auto-open add modal when navigated from another page with openAdd flag
    useEffect(() => {
        if ((location.state as { openAdd?: boolean } | null)?.openAdd) {
            setIsNewMissionModalOpen(true);
            window.history.replaceState({}, '');
        }
    }, [location.state]);
    const [newMissionFiles, setNewMissionFiles] = useState<File[]>([]);
    const [newMissionText, setNewMissionText] = useState('');

    // Global mission upload tracking (persists across page navigation)
    const { activeMissionUploads, startMissionMonitoring, dismissMissionUpload, updateMissionExecId, updateMissionDbId } = useProcessing();
    const { org } = useOrganization();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [scope, setScope] = useState<PortfolioScope>((searchParams.get('scope') as PortfolioScope) || 'portfolio');
    const [searchTerm, setSearchTerm] = useState('');
    const [clientFilter, setClientFilter] = useState<string>(searchParams.get('client') || 'all');
    const { members: orgMembers } = useOrgMembers();
    const { clients, createClient } = useClients();

    // Sync clientFilter when URL ?client= param changes (e.g. navigation from ClientsView)
    useEffect(() => {
        const clientParam = searchParams.get('client') || 'all';
        setClientFilter(clientParam);
        if (clientParam !== 'all' && scope === 'portfolio') {
            setScope('org');
        }
    }, [searchParams]);

    // Sync scope to URL
    const handleScopeChange = (newScope: PortfolioScope) => {
        setScope(newScope);
        setSearchParams(prev => { prev.set('scope', newScope); return prev; }, { replace: true });
    };

    // Fetch missions from database
    const { missions: dbMissions, isLoading: isMissionsLoading, updateMissionStatus: updateMissionStatusBackend, deleteMission: deleteMissionBackend, error, refresh, fetchMissionDetail } = useMissions(scope, user?.id);

    // Ref to prevent duplicate dismiss timers if the effect re-fires during the wait
    const pendingDismissRef = useRef<Set<string>>(new Set());

    // Auto-refresh when a mission processing completes (mirrors TalentPoolView pattern)
    // – 3s delay: lets the DB finish writing all fields before we fetch
    // – 12s widget visibility: user can see the "Terminé" banner before it disappears
    useEffect(() => {
        const newClosingIds = activeMissionUploads
            .filter(u => u.isClosing && !pendingDismissRef.current.has(u.id))
            .map(u => u.id);

        if (newClosingIds.length === 0) return;

        // Mark as pending so concurrent effect runs don't start duplicate timers
        newClosingIds.forEach(id => pendingDismissRef.current.add(id));

        setTimeout(() => {
            refresh().then(() => {
                setTimeout(() => {
                    newClosingIds.forEach(id => {
                        dismissMissionUpload(id);
                        pendingDismissRef.current.delete(id);
                    });
                }, 12000); // keep widget visible for 12s
            });
        }, 6000); // wait 6s for DB to settle before fetching
    }, [activeMissionUploads, refresh, dismissMissionUpload]);

    // Notifications modèle IA : fallback ou indisponibilité complète
    const notifiedMissionIdsRef = React.useRef<Set<number>>(new Set());
    useEffect(() => {
        dbMissions.forEach((m: any) => {
            if (notifiedMissionIdsRef.current.has(m.id)) return;
            const meta = m.summary_json?.meta;
            if (m.status === 'error' && m.error_details === 'GEMINI_UNAVAILABLE') {
                notifiedMissionIdsRef.current.add(m.id);
                setToast({ message: "⚠️ Le service d'analyse IA est temporairement indisponible. Réessayez dans quelques minutes.", visible: true });
                setTimeout(() => setToast({ message: '', visible: false }), 8000);
            } else if (m.status === 'Prospection' && meta?.fallback_used) {
                notifiedMissionIdsRef.current.add(m.id);
                setToast({ message: "⚡ Analyse effectuée avec le modèle de secours (service principal temporairement indisponible). La qualité peut légèrement varier.", visible: true });
                setTimeout(() => setToast({ message: '', visible: false }), 8000);
            }
        });
    }, [dbMissions]);

    // KANBAN STATE — populated from DB missions
    const [kanbanItems, setKanbanItems] = useState<KanbanItem[]>([]);

    // Sync URL param → open mission detail on load/navigation
    // If mission not found yet (e.g. toast "Voir" clicked before refresh completes), trigger a refresh
    useEffect(() => {
        if (urlMissionId && dbMissions.length > 0) {
            const found = dbMissions.find(m => String(m.id) === urlMissionId);
            if (found) {
                setSelectedMission(found);
                // Upgrade to full detail (summary_json) for the detail panel
                fetchMissionDetail(found.id).then(full => { if (full) setSelectedMission(full); });
            } else refresh(); // mission not in list yet — fetch again
        } else if (!urlMissionId) {
            setSelectedMission(null);
        }
    }, [urlMissionId, dbMissions]);

    // Sync kanban items when DB missions load
    useEffect(() => {
        if (dbMissions.length > 0) {
            // Build a contact lookup from clients data
            const contactMap = new Map<string, string>();
            clients.forEach(c => (c.contacts || []).forEach(cc => contactMap.set(cc.id, cc.full_name)));

            // Also build a client name lookup
            const clientNameMap = new Map<string, string>();
            clients.forEach(c => clientNameMap.set(c.id, c.name));

            const items: KanbanItem[] = dbMissions.map(m => {
                const resolvedClient = (m.client_id && clientNameMap.get(m.client_id)) || m.client;
                return {
                    id: m.id,
                    client: resolvedClient,
                    client_id: m.client_id || null,
                    contact_id: m.contact_id || null,
                    contact_name: m.contact_id ? (contactMap.get(m.contact_id) || null) : null,
                    title: m.title,
                    tjm: m.tjm || '',
                    location: m.location || '',
                    status: m.status,
                    matchScore: 0,
                    lastUpdate: m.datePosted || '',
                };
            });
            setKanbanItems(items);
        }
    }, [dbMissions, clients]);
    const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

    // --- KANBAN CONFIG (dynamic) ---
    const kanbanColumns = rawColumns.map(col => ({
        id: col.slug,
        title: `${col.emoji} ${col.label}`,
        headerStyle: COLOR_STYLES[col.color] ?? COLOR_STYLES.slate,
    }));

    // Distribute items into columns based on STATE (archived missions are excluded)
    const isArchived = (status: string) => status === 'Archivé' || status === 'Archive';
    const isProcessing = (status: string) => [
        'received', 'extraction_pdf', 'extraction_docx', 'analyse_ia', 'generation_ads', 'error', 'mission_existante'
    ].includes(status);
    const activeKanbanItems = kanbanItems.filter(i => {
        if (isArchived(i.status)) return false;
        if (isProcessing(i.status)) return false;
        if (clientFilter !== 'all' && i.client_id !== clientFilter) return false;
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return i.title.toLowerCase().includes(term) || i.client.toLowerCase().includes(term);
    });

    const getColumnItems = (slug: string) => {
        const colIndex = rawColumns.findIndex(c => c.slug === slug);
        if (colIndex < 0) return [];
        const allSlugs = new Set(rawColumns.map(c => c.slug));
        // First column gets all unknown/legacy statuses as fallback
        if (colIndex === 0) {
            return activeKanbanItems.filter(i =>
                i.status === slug || !i.status || !allSlugs.has(i.status)
            );
        }
        return activeKanbanItems.filter(i => i.status === slug);
    };

    const archivedCount = kanbanItems.filter(i => isArchived(i.status)).length;

    const getMatchBadgeStyle = (score: number) => {
        if (score >= 80) return "bg-emerald-100 text-emerald-700 border-emerald-200";
        if (score >= 60) return "bg-amber-100 text-amber-700 border-amber-200";
        return "bg-slate-100 text-slate-500 border-slate-200";
    };

    // --- DND SENSORS & STATE ---
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    const [activeDragItemId, setActiveDragItemId] = useState<string | null>(null);
    const [dragStartStatus, setDragStartStatus] = useState<string | null>(null);

    const handleDragStart = (event: DragStartEvent) => {
        const itemId = event.active.id as string;
        setActiveDragItemId(itemId);
        const item = kanbanItems.find(i => i.id === itemId);
        if (item) setDragStartStatus(item.status);

        setActiveMenuId(null);
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // Find the container based on item status or column slug
        const findStatus = (id: string) => {
            const col = rawColumns.find(c => c.slug === id);
            if (col) return col.slug;
            const item = kanbanItems.find(i => i.id === id);
            return item ? item.status : null;
        };

        const activeStatus = findStatus(activeId as string);
        const overStatus = findStatus(overId as string);

        if (!activeStatus || !overStatus) return;

        // Allowing drop on column even if empty
        if (activeStatus === overStatus) return;

        // Update status immediately during drag to visualize move
        setKanbanItems((prev) => {
            const activeItem = prev.find(i => i.id === activeId);
            if (!activeItem) return prev; // Should not happen

            return prev.map(item =>
                item.id === activeId ? { ...item, status: overStatus as string } : item
            );
        });
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDragItemId(null);

        if (!over) {
            return;
        }

        const activeId = active.id;
        const overId = over.id;

        // Use the status we captured at START of drag
        const originalStatus = dragStartStatus;

        // Determine the NEW status based on where we dropped
        let newStatusStr = '';
        const overColumn = rawColumns.find(c => c.slug === overId);
        if (overColumn) {
            newStatusStr = overColumn.slug;
        } else {
            // Dropped on an item
            const overItem = kanbanItems.find(i => i.id === overId);
            if (overItem) newStatusStr = overItem.status;
        }

        const hasStatusChanged = originalStatus && newStatusStr && originalStatus !== newStatusStr;

        // If status changed OR we moved to a different item (reorder), we process
        if (activeId !== overId || hasStatusChanged) {

            setKanbanItems((items) => {
                const oldIndex = items.findIndex((item) => item.id === activeId);
                const newIndex = items.findIndex((item) => item.id === overId);

                if (newIndex !== -1 && activeId !== overId) {
                    return arrayMove(items, oldIndex, newIndex);
                }
                return items;
            });

            // If we found a valid status AND it's different (or just to be safe), update backend
            if (newStatusStr && activeId && hasStatusChanged) {
                updateMissionStatusBackend(activeId as string, newStatusStr)
                    .catch(e => console.error("Failed to update status", e));
            }
        }
        setDragStartStatus(null);
    };


    // Initialize editor content when mission is selected — prefer real source text
    useEffect(() => {
        if (selectedMission) {
            if (selectedMission.originalJobText) {
                setEditorContent(selectedMission.originalJobText);
            } else {
                // Fallback: reconstruct from parsed fields
                setEditorContent(`TITRE DU POSTE: ${selectedMission.title}
CLIENT: ${selectedMission.client}

CONTEXTE DE LA MISSION:
${selectedMission.missionSummary}

CONTEXTE STRATÉGIQUE:
${selectedMission.strategicContext}

COMPÉTENCES REQUISES:
${selectedMission.requiredSkills.technical.map(s => `- ${s.name} (${s.importance})`).join('\n')}
${selectedMission.requiredSkills.methods.map(s => `- ${s.name} (${s.importance})`).join('\n')}

SOFT SKILLS:
${selectedMission.requiredSkills.soft.map(s => `- ${s.name} (${s.importance})`).join('\n')}`);
            }
        }
    }, [selectedMission]);

    const handleDownloadSource = () => {
        if (!selectedMission?.originalJobText) return;
        const blob = new Blob([selectedMission.originalJobText], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `source_${selectedMission.id}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // « Régénérer l'analyse » rejoue la mission **en place** (mode reanalyze de
    // l'edge function) : même ligne, donc matches, pipeline et rattachement client
    // conservés. Avant, on rouvrait la modale de création avec le texte source, ce
    // qui créait une seconde mission à chaque régénération — l'utilisateur qui
    // corrigeait juste le TJM se retrouvait avec un doublon.
    const handleRegenerateAnalysis = async () => {
        if (!selectedMission || isRegenerating) return;

        const missionId = selectedMission.id;
        const sourceText = editorContent.trim();
        const previousStatus = selectedMission.status;

        const flashToast = (message: string, ms = 4000) => {
            setToast({ message, visible: true });
            setTimeout(() => setToast({ message: '', visible: false }), ms);
        };

        if (!sourceText) {
            flashToast('Texte source vide — rien à ré-analyser.', 3000);
            return;
        }

        const missionWebhookUrl = import.meta.env.VITE_EDGE_CREATE_MISSION ?? import.meta.env.VITE_EDGE_CREATE_MISSION_REC;
        if (!missionWebhookUrl) {
            flashToast('⚠️ Variable VITE_EDGE_CREATE_MISSION / _REC manquante — vérifier Vercel env vars', 6000);
            return;
        }

        setIsRegenerating(true);
        const uiId = Date.now().toString();
        startMissionMonitoring(uiId, selectedMission.title, `mission_${missionId}`);
        updateMissionDbId(uiId, missionId);

        try {
            // Le mode reanalyze repart de `original_jd_text` : on y pousse d'abord le
            // texte de l'éditeur (corrections manuelles comprises). Le statut passe en
            // « analyse_ia » tout de suite, sinon le polling verrait encore
            // « Prospection » et annoncerait la fin avant même le début.
            const { error: saveErr } = await supabase
                .from('missions')
                .update({ original_jd_text: sourceText, status: 'analyse_ia', error_details: null } as any)
                .eq('id', Number(missionId));
            if (saveErr) throw saveErr;

            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch(missionWebhookUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                },
                body: JSON.stringify({ reanalyze: true, mission_id: Number(missionId) }),
            });
            if (!response.ok) throw new Error(await response.text().catch(() => 'Erreur réseau'));

            // Retour à la liste des missions : la ré-analyse tourne en tâche de fond
            // (widget de suivi + polling), alors que la fiche resterait figée sur un
            // brief en cours de remplacement.
            flashToast('Ré-analyse lancée');
            navigate('/missions');
        } catch (err) {
            console.error('Erreur lors de la ré-analyse :', err);
            dismissMissionUpload(uiId);
            // Le statut a pu être basculé avant l'échec : on le remet comme on l'a trouvé.
            await supabase.from('missions').update({ status: previousStatus } as any).eq('id', Number(missionId));
            flashToast('Échec du lancement de la ré-analyse.');
        } finally {
            setIsRegenerating(false);
        }
    };

    const handleImportFile = () => {
        if (importFiles.length > 0) {
            setEditorContent(`[Contenu extrait du fichier ${importFiles[0].name}...]
        
TITRE DU POSTE: ${selectedMission?.title}

NOUVEAU CONTENU IMPORTÉ:
L'analyse du document suggère une mise à jour des compétences requises...
        `);
            setIsUploadModalOpen(false);
            setImportFiles([]);
        }
    };

    const handleCreateMission = async () => {
        const file = newMissionFiles.length > 0 ? newMissionFiles[0] : null;
        const filename = file ? file.name : 'mission_texte.txt';
        const title = file ? file.name : 'Nouvelle Mission (Texte)';
        const uiId = Date.now().toString();

        // Pass filename so the polling can match this tracker to the right Supabase row
        startMissionMonitoring(uiId, title, filename);
        setToast({ message: "Analyse lancée ! Création de la mission...", visible: true });
        setIsNewMissionModalOpen(false);

        try {
            const formData = new FormData();
            if (newMissionText.trim()) formData.append('text', newMissionText);
            if (file) formData.append('file', file);
            if (org?.id) formData.append('organization_id', org.id);

            const missionWebhookUrl = import.meta.env.VITE_EDGE_CREATE_MISSION ?? import.meta.env.VITE_EDGE_CREATE_MISSION_REC;
            if (!missionWebhookUrl) {
                dismissMissionUpload(uiId);
                setToast({ message: '⚠️ Variable VITE_EDGE_CREATE_MISSION / _REC manquante — vérifier Vercel env vars', visible: true });
                setTimeout(() => setToast({ message: '', visible: false }), 6000);
                return;
            }

            const missionParams = new URLSearchParams();
            if (org?.id) missionParams.set('organization_id', org.id);
            if (user?.id) missionParams.set('created_by', user.id);
            const createUrl = missionParams.toString() ? `${missionWebhookUrl}?${missionParams}` : missionWebhookUrl;
            const { data: { session } } = await supabase.auth.getSession();
            const response = await fetch(createUrl, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                },
            });

            if (!response.ok) {
                // Quota de missions atteint : le message nomme la limite et propose la
                // montée en gamme, au lieu d'un « Erreur réseau » qui n'explique rien.
                const quotaInfo = parseQuotaExceeded(response.status, await response.json().catch(() => null));
                if (quotaInfo) {
                    dismissMissionUpload(uiId);
                    notify('error', 'Quota atteint', {
                        description: quotaExceededMessage(quotaInfo),
                        action: { label: 'Voir les forfaits', onClick: openUpgradeModal },
                    });
                    setToast({ message: quotaExceededMessage(quotaInfo), visible: true });
                    setTimeout(() => setToast({ message: '', visible: false }), 6000);
                    return;
                }
                throw new Error('Erreur réseau');
            }

            notifyQuotaChanged();

            // If webhook returns a real mission_id, bind it directly (faster than waiting for poll)
            try {
                const json = await response.json();
                console.log('[Mission] Webhook response:', json);
                const rawId = json?.mission_id;
                const missionId = rawId && !String(rawId).includes('{{') ? String(rawId) : null;
                if (missionId) {
                    console.log(`[Mission] Got mission_id=${missionId} — binding directly`);
                    updateMissionDbId(uiId, missionId);
                    if (user?.id) {
                        // await so created_by is stamped before the refresh
                        await supabase.from('missions').update({ created_by: user.id, assigned_to: user.id } as any).eq('id', Number(missionId));
                    }
                    // Refresh kanban immediately + again after edge function finishes analysis
                    refresh();
                    setTimeout(() => refresh(), 8000);
                }
            } catch (_) { /* not JSON — filename polling will handle it */ }

            setToast({ message: "Mission en cours d'analyse...", visible: true });
            setNewMissionFiles([]);
            setNewMissionText('');

        } catch (error) {
            console.error('Erreur lors de la création de mission :', error);
            setToast({ message: 'Erreur lors de la création.', visible: true });
            dismissMissionUpload(uiId);
        }
        setTimeout(() => setToast({ message: '', visible: false }), 3000);
    };

    const updateMissionStatus = async (id: string, newStatus: string) => {
        // Optimistic local update
        setKanbanItems(prev => prev.map(item =>
            item.id === id ? { ...item, status: newStatus as MissionDetails['status'] } : item
        ));
        setActiveMenuId(null);
        setToast({ message: `Statut mis à jour : ${newStatus}`, visible: true });
        setTimeout(() => setToast({ message: '', visible: false }), 2000);
        // Persist to Supabase
        await updateMissionStatusBackend(id, newStatus);
    };

    const deleteMission = async (id: string) => {
        setKanbanItems(prev => prev.filter(item => item.id !== id));
        setActiveMenuId(null);
        try {
            await deleteMissionBackend(id);
            setToast({ message: 'Mission supprimée', visible: true });
        } catch {
            setToast({ message: 'Erreur lors de la suppression', visible: true });
        }
        setTimeout(() => setToast({ message: '', visible: false }), 2500);
    };

    const archiveMission = async (id: string) => {
        setKanbanItems(prev => prev.map(item =>
            item.id === id ? { ...item, status: 'Archivé' } : item
        ));
        setActiveMenuId(null);
        setToast({ message: 'Mission archivée', visible: true });
        setTimeout(() => setToast({ message: '', visible: false }), 2000);
        await updateMissionStatusBackend(id, 'Archivé');
        if (selectedMission?.id === id) {
            navigate('/missions');
        }
    };

    const restoreMission = async (id: string) => {
        const firstSlug = rawColumns[0]?.slug || 'Prospection';
        const firstLabel = rawColumns[0]?.label || 'Prospection';
        setKanbanItems(prev => prev.map(item =>
            item.id === id ? { ...item, status: firstSlug } : item
        ));
        setToast({ message: `Mission restaurée en ${firstLabel}`, visible: true });
        setTimeout(() => setToast({ message: '', visible: false }), 2000);
        await updateMissionStatusBackend(id, firstSlug);
    };

    const copyToClipboard = (text: string, section: string) => {
        navigator.clipboard.writeText(text);
        setCopiedSection(section);
        setTimeout(() => setCopiedSection(null), 2000);
    };

    const getBadgeStyle = (category: 'tech' | 'method' | 'soft', importance: 'MUST_HAVE' | 'NICE_TO_HAVE') => {
        const baseClass = "px-3 py-1.5 rounded-lg text-xs font-bold transition-all border";

        if (category === 'tech') {
            return importance === 'MUST_HAVE'
                ? `${baseClass} bg-blue-600 text-white border-blue-600 shadow-sm`
                : `${baseClass} bg-white text-blue-600 border-blue-200 hover:border-blue-400`;
        }
        if (category === 'method') {
            return importance === 'MUST_HAVE'
                ? `${baseClass} bg-emerald-600 text-white border-emerald-600 shadow-sm`
                : `${baseClass} bg-white text-emerald-600 border-emerald-200 hover:border-emerald-400`;
        }
        if (category === 'soft') {
            return importance === 'MUST_HAVE'
                ? `${baseClass} bg-orange-500 text-white border-orange-500 shadow-sm`
                : `${baseClass} bg-white text-orange-600 border-orange-200 hover:border-orange-400`;
        }
        return baseClass;
    };

    const getSkillBadgeColor = (type: string) => {
        switch (type) {
            case 'tech': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'method': return 'bg-green-100 text-green-800 border-green-200';
            case 'soft': return 'bg-orange-100 text-orange-800 border-orange-200';
            default: return 'bg-slate-100 text-slate-700 border-slate-200';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Ouvert': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
            case 'Fermé': return 'text-red-600 bg-red-50 border-red-200';
            case 'En Pause': return 'text-amber-600 bg-amber-50 border-amber-200';
            default: return 'text-slate-600 bg-slate-50 border-slate-200';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Ouvert': return <PlayCircle size={16} />;
            case 'Fermé': return <StopCircle size={16} />;
            case 'En Pause': return <PauseCircle size={16} />;
            default: return <PlayCircle size={16} />;
        }
    };

    // Helper for Score Colors
    const getScoreTheme = (score: number) => {
        if (score >= 80) return { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-500' };
        if (score >= 70) return { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-500' };
        return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', ring: 'ring-red-500' };
    };

    // Helper for Criteria Icons
    const getCriteriaIcon = (status: string) => {
        switch (status) {
            case 'met': return <CheckCircle2 size={18} className="text-emerald-500 mt-0.5" />;
            case 'partial': return <AlertTriangle size={18} className="text-amber-500 mt-0.5" />;
            case 'missing': return <XCircle size={18} className="text-red-500 mt-0.5" />;
            default: return <div className="w-4 h-4 rounded-full bg-slate-200"></div>;
        }
    };

    const steps = rawColumns.map(col => col.label);

    if (selectedMission) {
        // --- MISSION DETAILS MODE (Full Page) ---
        return (
            <MissionDetailPanel
                mission={selectedMission}
                renderContent={() => (
            <div className="animate-in slide-in-from-right duration-300 pb-20 relative" onClick={() => setIsStatusOpen(false)}>

                {/* TOAST */}
                {toast.visible && (
                    <div className="fixed top-24 right-8 z-[100] flex items-center gap-3 px-4 py-3 bg-slate-800 text-white rounded-lg shadow-lg animate-in slide-in-from-right duration-300">
                        <CheckCircle2 size={18} className="text-emerald-400" />
                        <span className="font-medium text-sm">{toast.message}</span>
                    </div>
                )}

                {/* Fiche mission — bloc unique : toolbar (✕ + identité + actions) fusionnée avec le header */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4 relative z-20">
                    {/* Toolbar row (harmonisée avec la fiche talent : ✕ à gauche + identité + actions) */}
                    <div className="px-6 pt-4 pb-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <button
                                onClick={() => navigate('/missions')}
                                className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors shrink-0"
                                title="Fermer la fiche"
                                aria-label="Fermer la fiche"
                            >
                                <X size={18} />
                            </button>
                            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center font-bold text-white text-sm shrink-0">
                                {(selectedMission.client || selectedMission.title || '?').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-lg font-bold text-slate-900 truncate">{selectedMission.title}</h2>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <AssignDropdown
                                table="missions"
                                rowId={selectedMission.id}
                                assignedTo={selectedMission.assigned_to ?? null}
                                members={orgMembers}
                                onAssigned={() => refresh()}
                            />
                            <button
                                onClick={() => archiveMission(selectedMission.id)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            >
                                <Archive size={14} />
                                Archiver
                            </button>
                        </div>
                    </div>

                    {/* B. Header body */}
                    <div className="px-6 pb-6 pt-0">
                    <div className="flex flex-col gap-4">
                        {/* TOP ROW: Title & Client */}
                        <div>
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                {/* Badge AI uniquement si aucun client lié — le select affiche déjà le nom sinon */}
                                {!selectedMission.client_id && selectedMission.client && (
                                    <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-bold tracking-wide border border-slate-200">
                                        {selectedMission.client}
                                    </span>
                                )}
                                {/* Client link / selector */}
                                <select
                                    value={selectedMission.client_id || ""}
                                    onChange={async (e) => {
                                        const cid = e.target.value;
                                        if (cid === "__CREATE__") {
                                            const newName = window.prompt("Nom du nouveau client :");
                                            if (newName && newName.trim() && org) {
                                                const newClient = await createClient({ name: newName.trim(), organization_id: org.id, created_by: user?.id || null });
                                                if (newClient) {
                                                    await supabase.from('missions').update({ client_id: newClient.id, client_name: newClient.name } as any).eq('id', Number(selectedMission.id));
                                                    refresh();
                                                }
                                            }
                                            return;
                                        }
                                        if (cid === "__UNLINK__") {
                                            await supabase.from('missions').update({ client_id: null, client_name: null, contact_id: null } as any).eq('id', Number(selectedMission.id));
                                            refresh();
                                            return;
                                        }
                                        if (!cid) return;
                                        const cl = clients.find(c => c.id === cid);
                                        await supabase.from('missions').update({ client_id: cid, client_name: cl?.name || null, contact_id: null } as any).eq('id', Number(selectedMission.id));
                                        refresh();
                                    }}
                                    className="px-2 py-1 text-xs border border-dashed border-slate-300 rounded-md text-slate-500 bg-white hover:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 cursor-pointer"
                                >
                                    <option value="" disabled>🔗 Lier à un client…</option>
                                    {clients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                    <option disabled>──────</option>
                                    <option value="__CREATE__">➕ Créer un nouveau client...</option>
                                    {selectedMission.client_id && <option value="__UNLINK__">❌ Détacher le client</option>}
                                </select>
                                {selectedMission.client_id && (
                                    <button
                                        onClick={() => navigate(`/clients/${selectedMission.client_id}`)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-200 hover:bg-amber-100 transition-colors"
                                    >
                                        <Building2 size={12} /> Voir fiche
                                    </button>
                                )}
                                {/* Contact selector */}
                                {(() => {
                                    const linkedClient = selectedMission.client_id ? clients.find(c => c.id === selectedMission.client_id) : null;
                                    const contacts = linkedClient?.contacts || [];
                                    if (!linkedClient || contacts.length === 0) return null;
                                    const currentContact = contacts.find(cc => cc.id === selectedMission.contact_id);
                                    if (currentContact) {
                                        return (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-200">
                                                <User size={12} /> {currentContact.full_name}
                                                {currentContact.role && <span className="text-indigo-400 font-normal">· {currentContact.role}</span>}
                                                <button
                                                    onClick={async () => {
                                                        await supabase.from('missions').update({ contact_id: null } as any).eq('id', Number(selectedMission.id));
                                                        refresh();
                                                    }}
                                                    className="ml-1 text-indigo-300 hover:text-red-500 transition-colors"
                                                    title="Retirer le contact"
                                                >
                                                    <X size={10} />
                                                </button>
                                            </span>
                                        );
                                    }
                                    return (
                                        <select
                                            value=""
                                            onChange={async (e) => {
                                                const contactId = e.target.value;
                                                if (!contactId) return;
                                                await supabase.from('missions').update({ contact_id: contactId } as any).eq('id', Number(selectedMission.id));
                                                refresh();
                                            }}
                                            className="px-2 py-1 text-xs border border-dashed border-indigo-300 rounded-md text-indigo-500 bg-white hover:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                                        >
                                            <option value="">👤 Associer un contact…</option>
                                            {contacts.map(cc => (
                                                <option key={cc.id} value={cc.id}>{cc.full_name}{cc.role ? ` (${cc.role})` : ''}</option>
                                            ))}
                                        </select>
                                    );
                                })()}
                            </div>
                            <p className="text-sm text-slate-400 font-medium mb-4">Alternative : {selectedMission.standardized_title || "Titre standardisé non disponible"}</p>
                        </div>

                        {/* MIDDLE ROW: Stepper (Interactive Status Switcher) */}
                        <div className="flex items-center gap-2 overflow-x-auto pb-2">
                            {steps.map((step, idx) => {
                                // Map step names to DB status values for Kanban sync
                                const stepToStatus: Record<string, string> = {};
                                rawColumns.forEach(col => { stepToStatus[col.label] = col.slug; });
                                return (
                                    <div key={idx} className="flex items-center shrink-0">
                                        <button
                                            onClick={async () => {
                                                const prevStep = currentStep;
                                                // Optimistic UI update
                                                setCurrentStep(idx);
                                                const dbStatus = stepToStatus[step] || step;
                                                // Update local kanban items for instant Kanban sync
                                                setKanbanItems(prev => prev.map(item =>
                                                    item.id === selectedMission.id ? { ...item, status: dbStatus } : item
                                                ));
                                                // Update selectedMission locally
                                                setSelectedMission(prev => prev ? { ...prev, status: dbStatus as any } : prev);
                                                // Show toast
                                                setToast({ message: `Statut mis à jour : ${step}`, visible: true });
                                                setTimeout(() => setToast({ message: '', visible: false }), 3000);
                                                // Persist to Supabase
                                                try {
                                                    await updateMissionStatusBackend(selectedMission.id, dbStatus);
                                                } catch {
                                                    // Revert on failure
                                                    setCurrentStep(prevStep);
                                                    setToast({ message: 'Erreur lors de la mise à jour du statut', visible: true });
                                                    setTimeout(() => setToast({ message: '', visible: false }), 3000);
                                                }
                                            }}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${idx === currentStep
                                                ? 'bg-amber-50 text-amber-700 border-amber-200 ring-2 ring-amber-100'
                                                : idx < currentStep
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                    : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100 hover:text-slate-600'
                                                }`}>
                                            {idx < currentStep && <CheckCircle2 size={12} />}
                                            {idx === currentStep && <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />}
                                            {step}
                                        </button>
                                        {idx < steps.length - 1 && (
                                            <div className={`w-6 h-0.5 mx-1 ${idx < currentStep ? 'bg-emerald-200' : 'bg-slate-100'}`} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* BOTTOM ROW: Horizontal Vitals Bar */}
                        <div className="grid grid-cols-4 gap-4 pt-4 border-t border-slate-100 w-full">
                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="p-2 bg-blue-50 text-blue-600 rounded-md shadow-sm shrink-0">
                                    <MapPin size={18} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Localisation</p>
                                    <p className="text-sm font-bold text-slate-700 truncate">{selectedMission.location}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="p-2 bg-purple-50 text-purple-600 rounded-md shadow-sm shrink-0">
                                    <Briefcase size={18} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Mode</p>
                                    <p className="text-sm font-bold text-slate-700">Hybride</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="p-2 bg-amber-50 text-amber-600 rounded-md shadow-sm shrink-0">
                                    <Users size={18} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Séniorité</p>
                                    <p className="text-sm font-bold text-slate-700 capitalize">Senior</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                                <div className="p-2 bg-green-50 text-green-600 rounded-md shadow-sm shrink-0">
                                    <Euro size={18} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">TJM / Salaire</p>
                                    <p className="text-sm font-bold text-slate-700 truncate">{selectedMission.tjm}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/*
            ============================================
            TABBED NAVIGATION (Updated Labels)
            ============================================
        */}
                <div className="border-b border-slate-200 mb-8">
                    <div className="flex gap-8">
                        <button
                            onClick={() => navigate(`/missions/${selectedMission.id}`)}
                            className={`pb-4 px-2 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'brief'
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                }`}
                        >
                            <FileText size={18} />
                            Analyse
                        </button>
                        <button
                            onClick={() => navigate(`/missions/${selectedMission.id}/talents`)}
                            className={`pb-4 px-2 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'talents'
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                }`}
                        >
                            <Users size={18} />
                            Talents Matchés
                        </button>
                        <button
                            onClick={() => navigate(`/missions/${selectedMission.id}/source`)}
                            className={`pb-4 px-2 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'source'
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                }`}
                        >
                            <FileCode size={18} />
                            Source
                        </button>
                        <button
                            onClick={() => navigate(`/missions/${selectedMission.id}/pipeline`)}
                            className={`pb-4 px-2 text-sm font-bold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'pipeline'
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                }`}
                        >
                            <Columns2 size={18} />
                            Pipeline
                        </button>
                    </div>
                </div>

                {/* ... (TAB CONTENTS are managed below) ... */}

                {/* 
            ============================================
            TAB CONTENT: BRIEF & ANALYSE
            ============================================
        */}
                {activeTab === 'brief' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        {/* 
                    SECTION 0: RÉSUMÉ DU BESOIN (HERO) - Typography Fixed
                */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full -mr-10 -mt-10 z-0"></div>
                            <div className="relative z-10">
                                <h3 className="text-slate-900 font-bold flex items-center gap-2 mb-2 text-base">
                                    <FileText size={18} className="text-amber-500" />
                                    Résumé du Besoin
                                </h3>
                                <p className="font-sans text-slate-700 text-sm leading-relaxed font-medium">
                                    {selectedMission.missionSummary || "Aucun résumé disponible pour cette mission."}
                                </p>
                            </div>
                        </div>

                        {/* 
                    SECTION 1: STRATEGIC INTELLIGENCE (UPDATED LISTS)
                */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                            {/* Left: Deal Breakers */}
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 shadow-sm">
                                <h3 className="text-red-900 font-bold flex items-center gap-2 mb-4 text-base">
                                    <AlertOctagon size={18} className="text-red-600" />
                                    Deal Breakers
                                </h3>
                                <div className="space-y-3">
                                    {selectedMission.dealBreakers.length > 0 ? selectedMission.dealBreakers.map((db, i) => (
                                        <div key={i} className="flex items-start gap-3">
                                            <div className="mt-0.5 text-red-500 shrink-0">
                                                <XCircle size={16} fill="currentColor" className="text-red-200" />
                                            </div>
                                            <span className="font-semibold text-red-900 text-xs leading-relaxed">{db}</span>
                                        </div>
                                    )) : (
                                        <p className="text-red-800/70 italic text-xs">Aucun critère éliminatoire spécifique.</p>
                                    )}
                                </div>
                            </div>

                            {/* Right: Strategic Info */}
                            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-5">
                                    <Zap size={80} className="text-indigo-900" />
                                </div>

                                <h3 className="text-indigo-900 font-bold flex items-center gap-2 mb-4 text-base relative z-10">
                                    <Zap size={18} className="text-indigo-600" />
                                    Informations Stratégiques
                                </h3>

                                <div className="relative z-10 space-y-1.5">
                                    {selectedMission.strategicContext
                                        ? selectedMission.strategicContext.split('\n').filter(Boolean).map((note, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                                <span className="text-indigo-400 shrink-0 mt-0.5">•</span>
                                                <p className="text-indigo-900 text-xs font-semibold leading-relaxed">{note}</p>
                                            </div>
                                        ))
                                        : <p className="text-indigo-400 text-xs italic">Aucune information stratégique renseignée.</p>
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Risks Identified */}
                        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-4">
                            <h3 className="text-slate-900 font-bold flex items-center gap-2 mb-4 text-base">
                                <AlertTriangle size={18} className="text-orange-500" />
                                Risques Identifiés
                            </h3>
                            <div className="space-y-2">
                                {selectedMission.risks && selectedMission.risks.length > 0 ? selectedMission.risks.map((risk, idx) => (
                                    <div key={idx} className="flex items-start gap-3 p-2.5 rounded-lg border border-orange-100 bg-orange-50/50">
                                        <AlertTriangle size={14} className="text-orange-500 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-xs font-medium text-orange-800 leading-snug">{risk}</p>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="text-slate-500 italic text-xs">Aucun risque majeur identifié.</p>
                                )}
                            </div>
                        </div>
                        {/* 
                    SECTION 2: REQUIREMENTS MATRIX
                */}
                        <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-8 overflow-hidden">
                            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">

                                {/* Column 1: Tech */}
                                <div className="p-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                            <Brain size={20} />
                                        </div>
                                        <h3 className="font-bold text-slate-900 text-lg">Technique</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedMission.requiredSkills.technical.map((skill, idx) => (
                                            <span key={idx} className={getBadgeStyle('tech', skill.importance)}>
                                                {skill.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Column 2: Methods */}
                                <div className="p-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2 bg-emerald-100 text-emerald-600 rounded-lg">
                                            <Hammer size={20} />
                                        </div>
                                        <h3 className="font-bold text-slate-900 text-lg">Méthodes & Outils</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedMission.requiredSkills.methods.map((skill, idx) => (
                                            <span key={idx} className={getBadgeStyle('method', skill.importance)}>
                                                {skill.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Column 3: Soft Skills */}
                                <div className="p-6">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                                            <Heart size={20} />
                                        </div>
                                        <h3 className="font-bold text-slate-900 text-lg">Soft Skills</h3>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedMission.requiredSkills.soft.map((skill, idx) => (
                                            <span key={idx} className={getBadgeStyle('soft', skill.importance)}>
                                                {skill.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                            </div>
                            {/* Legend */}
                            <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 flex items-center gap-6 text-xs text-slate-500 font-medium">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded bg-slate-800"></div>
                                    <span>Must Have</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded border border-slate-400 bg-white"></div>
                                    <span>Nice to Have</span>
                                </div>
                            </div>
                        </div>

                        {/* 
                    SECTION 3: ACTION KITS
                */}
                        <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                            <Copy size={24} className="text-slate-400" />
                            Kits d'Argumentaire
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Kit Commercial */}
                            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col">
                                <div className="flex justify-between items-start mb-6 border-b border-slate-100 pb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                                            <Briefcase size={20} />
                                        </div>
                                        <h4 className="font-bold text-slate-900 text-lg">Kit Commercial</h4>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setIsAdModalOpen(true)}
                                            className="flex items-center gap-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                                        >
                                            <Sparkles size={14} />
                                            Générer une offre
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    {/* Boolean Search */}
                                    <div>
                                        <div className="flex justify-between items-center mb-2">
                                            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                <SearchIcon size={14} /> Recherche Booléenne
                                            </h5>
                                            <button
                                                onClick={() => copyToClipboard(selectedMission.booleanSearch || '', 'boolean')}
                                                className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 font-bold transition-colors bg-blue-50 px-2 py-1 rounded"
                                            >
                                                <Copy size={12} /> COPIER
                                            </button>
                                        </div>
                                        <div className="bg-slate-900 rounded-lg p-3 group relative shadow-inner">
                                            <code className="text-[10px] text-emerald-400 font-mono break-words leading-relaxed block">
                                                {selectedMission.booleanSearch || "Non disponible"}
                                            </code>
                                        </div>
                                    </div>

                                    {/* Questions Tabs */}
                                    <div>
                                        <div className="flex justify-between items-center mb-3">
                                            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                <HelpCircle size={14} /> Questions
                                            </h5>
                                            <button
                                                onClick={() => {
                                                    const questions = activeQuestionTab === 'technical'
                                                        ? selectedMission.questions_technical_scope
                                                        : selectedMission.questions_admin_contractual;
                                                    const text = questions?.map(q =>
                                                        typeof q === 'object' && q !== null
                                                            ? `${(q as any).question}${(q as any).hint ? `\n  → ${(q as any).hint}` : ''}`
                                                            : q
                                                    ).join('\n') || '';
                                                    copyToClipboard(text, 'questions');
                                                }}
                                                className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-700 font-bold transition-colors bg-blue-50 px-2 py-1 rounded"
                                            >
                                                <Copy size={12} /> COPIER
                                            </button>
                                        </div>

                                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                                            <div className="flex border-b border-slate-200">
                                                <button
                                                    onClick={() => setActiveQuestionTab('technical')}
                                                    className={`flex-1 py-2 text-xs font-bold text-center transition-colors ${activeQuestionTab === 'technical'
                                                        ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                                                        : 'text-slate-500 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    Techniques
                                                </button>
                                                <button
                                                    onClick={() => setActiveQuestionTab('contractual')}
                                                    className={`flex-1 py-2 text-xs font-bold text-center transition-colors ${activeQuestionTab === 'contractual'
                                                        ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-500'
                                                        : 'text-slate-500 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    Contractuelles
                                                </button>
                                            </div>

                                            <div className="p-0">
                                                <ul className="divide-y divide-slate-100">
                                                    {activeQuestionTab === 'technical' ? (
                                                        selectedMission.questions_technical_scope?.length ? (
                                                            selectedMission.questions_technical_scope.map((q, idx) => {
                                                                const isObj = typeof q === 'object' && q !== null;
                                                                return (
                                                                    <li key={idx} className="p-3 text-xs text-slate-700 hover:bg-slate-50 transition-colors">
                                                                        <div className="flex gap-2">
                                                                            <span className="text-blue-400 font-bold shrink-0">•</span>
                                                                            <span className="font-semibold">{isObj ? (q as any).question : q}</span>
                                                                        </div>
                                                                        {isObj && (q as any).hint && (
                                                                            <p className="mt-1.5 ml-4 text-slate-400 italic leading-snug">{(q as any).hint}</p>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })
                                                        ) : (
                                                            <li className="p-4 text-center text-xs text-slate-400 italic">Aucune question technique.</li>
                                                        )
                                                    ) : (
                                                        selectedMission.questions_admin_contractual?.length ? (
                                                            selectedMission.questions_admin_contractual.map((q, idx) => {
                                                                const isObj = typeof q === 'object' && q !== null;
                                                                return (
                                                                    <li key={idx} className="p-3 text-xs text-slate-700 hover:bg-slate-50 transition-colors">
                                                                        <div className="flex gap-2">
                                                                            <span className="text-purple-400 font-bold shrink-0">•</span>
                                                                            <span className="font-semibold">{isObj ? (q as any).question : q}</span>
                                                                        </div>
                                                                        {isObj && (q as any).hint && (
                                                                            <p className="mt-1.5 ml-4 text-slate-400 italic leading-snug">{(q as any).hint}</p>
                                                                        )}
                                                                    </li>
                                                                );
                                                            })
                                                        ) : (
                                                            <li className="p-4 text-center text-xs text-slate-400 italic">Aucune question contractuelle.</li>
                                                        )
                                                    )}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Kit Candidat */}
                            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col h-full">
                                <div className="flex justify-between items-start mb-6 border-b border-slate-100 pb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                                            <User size={20} />
                                        </div>
                                        <h4 className="font-bold text-slate-900 text-lg">Kit Candidat</h4>
                                    </div>
                                </div>

                                <div className="space-y-8 flex-1 overflow-y-auto pr-2">

                                    {/* 1. PORTRAIT ROBOT (Persona) */}
                                    <div>
                                        <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <Target size={14} className="text-amber-500" />
                                            Portrait Robot
                                        </h5>
                                        <div className="bg-amber-50/50 rounded-xl p-4 border border-amber-100 space-y-3">
                                            {/* Standardized Title */}
                                            {selectedMission.standardized_title && (
                                                <div className="flex items-center gap-2 pb-3 border-b border-amber-100/50">
                                                    <span className="px-2 py-0.5 bg-white text-amber-700 text-[10px] font-bold uppercase tracking-wide rounded border border-amber-100 shadow-sm">
                                                        Titre Cible
                                                    </span>
                                                    <span className="text-sm font-bold text-slate-800">
                                                        {selectedMission.standardized_title}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Persona Description */}
                                            <div className="flex gap-3">
                                                <div className="mt-1 min-w-[4px] w-1 rounded-full bg-amber-300"></div>
                                                <div>
                                                    <p className="text-sm text-slate-700 leading-relaxed italic">
                                                        "{selectedMission.persona || "Persona non défini."}"
                                                    </p>
                                                    {/* Merged Type Candidat Info if available */}
                                                    {selectedMission.deal_summary_one_line && (
                                                        <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-amber-100/50">
                                                            <span className="font-semibold text-amber-700">Insight:</span> {selectedMission.deal_summary_one_line}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2. À SAVOIR (Badges) — HIGHLIGHT-1 : « Points clés (Highlights) »
                                        promettait du remarquable et livrait le pied de page du PDF
                                        (SIRET, TVA, adresse de l'émetteur). Le libellé dit maintenant
                                        ce que le bloc contient : les conditions de mission qu'aucun
                                        autre champ de la fiche ne porte. */}
                                    {selectedMission.other_explicit_info && selectedMission.other_explicit_info.length > 0 && (
                                        <div>
                                            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                                <Zap size={14} className="text-indigo-500" />
                                                À savoir
                                            </h5>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedMission.other_explicit_info.map((info, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg border border-indigo-100 shadow-sm hover:bg-indigo-100 transition-colors cursor-default"
                                                    >
                                                        {info}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* 3. VALIDATION (Screening Table) */}
                                    <div>
                                        <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                            <CheckCircle2 size={14} className="text-emerald-500" />
                                            Validation (Screening)
                                        </h5>
                                        {selectedMission.kitDetails.screeningQuestions.length === 0 ? (
                                            <p className="text-xs text-slate-400 italic px-2">Aucune question de screening définie.</p>
                                        ) : (
                                            <div className="rounded-xl border border-slate-200 overflow-hidden">
                                                {/* Table header */}
                                                <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 px-3 py-2 gap-3">
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Question à poser</span>
                                                    <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">✅ Ce qu'il faut entendre</span>
                                                    <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">❌ À éviter</span>
                                                </div>
                                                {/* Rows */}
                                                {selectedMission.kitDetails.screeningQuestions.map((q, idx) => {
                                                    const isObj = typeof q === 'object' && q !== null;
                                                    const text = isObj ? (q as any).question : q;
                                                    const greenFlag = isObj ? ((q as any).green_flags || (q as any).criteria || '') : '';
                                                    const redFlag = isObj ? ((q as any).red_flags || '') : '';
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="grid grid-cols-3 gap-3 px-3 py-3 items-start border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors"
                                                        >
                                                            <p className="text-xs text-slate-700 font-medium leading-snug">{text}</p>
                                                            <p className="text-xs text-emerald-700 leading-snug italic">
                                                                {greenFlag || '—'}
                                                            </p>
                                                            <p className="text-xs text-red-600 leading-snug italic">
                                                                {redFlag || '—'}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>


                                </div>
                            </div>
                        </div>
                    </div>
                )}


                {/* 
            ============================================
            TAB CONTENT: TALENTS MATCHÉS (IMPROVED ACCORDION)
            ============================================
        */}
                {activeTab === 'talents' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">

                        <MissionMatchResults
                            missionId={Number(selectedMission?.id)}
                            onLaunchMatching={() => navigate('/matching', { state: { preSelectedMissionId: selectedMission?.id } })}
                        />

                    </div>
                )}

                {activeTab === 'source' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 flex flex-col h-[calc(100vh-450px)] min-h-[300px]">
                        {/* Header (Flex-none) */}
                        <div className="flex-none mb-4 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900">Éditeur de l'Appel d'Offres</h2>
                                <p className="text-slate-500 text-xs mt-0.5">
                                    Modifiez le texte original ci-dessous pour régénérer l'analyse.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleDownloadSource}
                                    disabled={!selectedMission?.originalJobText}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-700 text-xs font-bold hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Download size={14} />
                                    Télécharger la source
                                </button>
                            </div>
                        </div>

                        {/* View Mode Toggle */}
                        <div className="flex border-b border-slate-200 mb-4 bg-white/50 p-1 rounded-lg self-start">
                            <button
                                onClick={() => setSourceViewMode('editor')}
                                className={`px-4 py-1.5 text-sm font-bold rounded-md transition-colors ${sourceViewMode === 'editor' ? 'bg-amber-100 text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                            >
                                Éditeur (Input)
                            </button>
                            <button
                                onClick={() => setSourceViewMode('json')}
                                className={`px-4 py-1.5 text-sm font-bold rounded-md transition-colors ${sourceViewMode === 'json' ? 'bg-amber-100 text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                            >
                                Résultat JSON (Output)
                            </button>
                        </div>

                        {/* Content Area (Flex-1) */}
                        <div className="flex-1 relative group min-h-0">
                            {sourceViewMode === 'editor' ? (
                                <>
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-slate-200 to-slate-100 rounded-xl blur opacity-50 group-hover:opacity-100 transition duration-200"></div>
                                    <textarea
                                        className="relative w-full h-full p-6 bg-slate-50 rounded-xl font-mono text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/50 border border-slate-200 resize-none shadow-inner leading-relaxed overflow-y-auto"
                                        value={editorContent}
                                        onChange={(e) => setEditorContent(e.target.value)}
                                    />
                                </>
                            ) : (
                                <div className="absolute inset-0 bg-slate-900 rounded-xl p-4 overflow-y-auto shadow-inner border border-slate-800">
                                    <pre className="font-mono text-xs text-blue-300 leading-relaxed">
                                        <code>{selectedMission?.summary_json ? JSON.stringify(selectedMission.summary_json, null, 2) : 'Aucun output JSON disponible.'}</code>
                                    </pre>
                                </div>
                            )}
                        </div>

                        {/* Action Bar (Flex-none) */}
                        <div className="flex-none pt-4 bg-white border-t border-slate-200 flex justify-end mt-4 rounded-b-xl">
                            <button
                                onClick={() => setShowRegenConfirm(true)}
                                disabled={sourceViewMode === 'json' || isRegenerating}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all shadow-md hover:-translate-y-0.5 ${sourceViewMode === 'json' || isRegenerating ? 'bg-slate-400 cursor-not-allowed shadow-none' : 'bg-amber-600 hover:bg-amber-700 text-white hover:shadow-lg'}`}
                            >
                                <RefreshCw size={18} className={isRegenerating ? 'animate-spin' : undefined} />
                                {isRegenerating ? 'Ré-analyse en cours…' : "Régénérer l'analyse"}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === 'pipeline' && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                        <PipelineKanbanView missionId={Number(selectedMission.id)} />
                    </div>
                )}

                {/* REGENERATION CONFIRMATION MODAL */}
                {showRegenConfirm && (
                    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
                        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setShowRegenConfirm(false)} />
                        <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md p-6 animate-in zoom-in-95 duration-200">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 bg-amber-50 rounded-xl">
                                    <RefreshCw size={22} className="text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Régénérer l'analyse ?</h3>
                                    <p className="text-xs text-slate-500">{selectedMission.title}</p>
                                </div>
                            </div>

                            <p className="text-sm text-amber-600 text-center mb-6 leading-relaxed">
                                La mission sera <span className="font-bold">ré-analysée</span>, à partir du texte source affiché : le brief actuel (résumé, compétences, annonces) sera <span className="font-bold">remplacé</span> et il faudra lancer un <span className="font-bold">nouveau matching</span>.
                            </p>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowRegenConfirm(false)}
                                    className="flex-1 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={async () => {
                                        setShowRegenConfirm(false);
                                        await handleRegenerateAnalysis();
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition-colors shadow-sm"
                                >
                                    <RefreshCw size={16} />
                                    Confirmer
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/*
            ============================================
            SLIDE-OVER DRAWER (FULL PROFILE) & CONTACT MODAL
            ============================================
        */}
                {drawerCandidate && (
                    <div className="fixed inset-0 z-[60] flex justify-end" onClick={(e) => e.stopPropagation()}>
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
                            onClick={() => setDrawerCandidate(null)}
                        ></div>

                        {/* Drawer Container */}
                        <div className="relative w-full lg:w-[50%] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">

                            {/* Header */}
                            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white z-10">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold">
                                        {drawerCandidate.initials}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900">{drawerCandidate.name}</h3>
                                        <p className="text-sm text-slate-500">{drawerCandidate.role}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setDrawerCandidate(null)}
                                        className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Placeholder Content */}
                            <div className="flex-1 flex flex-col items-center justify-center bg-slate-50/50 p-8 text-center">
                                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mb-4 animate-pulse">
                                    <FileText size={32} className="text-slate-400" />
                                </div>
                                <h4 className="text-slate-900 font-semibold mb-2">Chargement du CV complet...</h4>
                                <p className="text-slate-500 text-sm max-w-xs">
                                    La vue détaillée du profil candidat sera disponible prochainement.
                                </p>
                            </div>

                            {/* Footer Actions */}
                            <div className="p-4 border-t border-slate-200 bg-white flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setToast({ message: 'Téléchargement du CV...', visible: true });
                                        setTimeout(() => setToast({ ...toast, visible: false }), 2000);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors text-slate-700"
                                >
                                    <Download size={16} /> Télécharger le CV
                                </button>
                                <button
                                    onClick={() => { setContactCandidate(drawerCandidate); }}
                                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 shadow-sm transition-colors"
                                >
                                    <Mail size={16} /> Contacter
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {contactCandidate && (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
                        <div
                            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                            onClick={() => setContactCandidate(null)}
                        ></div>
                        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden animate-in zoom-in-95 duration-200">
                            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                                <h3 className="font-bold text-lg text-slate-800">Coordonnées du Candidat</h3>
                                <button
                                    onClick={() => setContactCandidate(null)}
                                    className="p-1 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors group cursor-pointer border border-transparent hover:border-slate-200">
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><Phone size={20} /></div>
                                    <div className="flex-1"><div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Téléphone</div><div className="text-slate-900 font-medium">{contactCandidate.phone || 'Non renseigné'}</div></div>
                                </div>
                                <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors group cursor-pointer border border-transparent hover:border-slate-200">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0"><Mail size={20} /></div>
                                    <div className="flex-1"><div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Email</div><div className="text-slate-900 font-medium">{contactCandidate.email || 'Non renseigné'}</div></div>
                                </div>
                                <div className="flex items-center gap-4 p-3 border border-transparent">
                                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><MapPin size={20} /></div>
                                    <div className="flex-1"><div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Localisation</div><div className="text-slate-900 font-medium">{contactCandidate.location || 'Non renseigné'}</div></div>
                                </div>
                            </div>
                            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                                <button onClick={() => setContactCandidate(null)} className="px-6 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm">Fermer</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* AD GENERATOR MODAL */}
                {selectedMission && (
                    <MissionAdGeneratorModal
                        isOpen={isAdModalOpen}
                        onClose={() => setIsAdModalOpen(false)}
                        missionData={selectedMission}
                        generatedContent={buildGeneratedContent(selectedMission)}
                    />
                )}
            </div>
                )}
            />
        );
    }

    // --- KANBAN BOARD VIEW (Replaces Grid) ---
    return (
        <div className="animate-in fade-in duration-500 h-full flex flex-col relative" onClick={() => setActiveMenuId(null)}>

            {/* TOAST CONTAINER FOR DASHBOARD */}
            {toast.visible && (
                <div className="fixed top-24 right-8 z-[100] flex items-center gap-3 px-4 py-3 bg-slate-800 text-white rounded-lg shadow-lg animate-in slide-in-from-right duration-300">
                    <CheckCircle2 size={18} className="text-emerald-400" />
                    <span className="font-medium text-sm">{toast.message}</span>
                </div>
            )}

            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Missions</h1>
                    <p className="mt-1 text-sm text-slate-500">Gérez vos missions et Appel d'offre.</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <ScopeToggle scope={scope} onChange={handleScopeChange} orgName={org?.name} entityLabel="Missions" />

                    {/* Search Bar */}
                    <div className="relative w-full sm:w-64">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <SearchIcon className="text-amber-500" size={16} />
                        </div>
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all shadow-sm text-xs text-orange-600 placeholder-slate-400"
                        />
                    </div>

                    {/* Client Filter */}
                    {clients.length > 0 && (
                        <select
                            value={clientFilter}
                            onChange={(e) => {
                                const val = e.target.value;
                                setClientFilter(val);
                                if (val !== 'all' && scope === 'portfolio') {
                                    handleScopeChange('org');
                                }
                            }}
                            className={`px-3 py-2 bg-white border rounded-lg text-xs font-medium transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 ${clientFilter !== 'all' ? 'border-amber-400 text-amber-700 bg-amber-50' : 'border-slate-200 text-slate-600'}`}
                        >
                            <option value="all">Tous les clients</option>
                            {clients.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    )}

                    {archivedCount > 0 && (
                        <button
                            onClick={() => setShowArchived(!showArchived)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border rounded-lg transition-colors ${showArchived ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-slate-500 bg-slate-50 border-slate-200 hover:bg-slate-100'}`}
                        >
                            <Archive size={14} />
                            {archivedCount} archivée{archivedCount > 1 ? 's' : ''}
                        </button>
                    )}

                    <button
                        onClick={() => setIsNewMissionModalOpen(true)}
                        className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-sm shadow-amber-200 text-sm"
                    >
                        <Plus size={16} />
                        Nouvelle Mission
                    </button>
                </div>
            </div>

            {/* PROCESSING WIDGETS (Active Imports) — persists across page navigation */}
            <div className="px-6">
                {activeMissionUploads.map(upload => (
                    <MissionProgressWidget
                        key={`widget-${upload.id}`}
                        title={upload.title}
                        status={upload.status}
                        canDismiss={true}
                        onDismiss={() => dismissMissionUpload(upload.id)}
                    />
                ))}
            </div>

            {/* KANBAN BOARD */}
            <div className="flex-1 overflow-x-auto pb-4">
                <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    <div className="flex h-full gap-6">
                        {kanbanColumns.map(column => {
                            const columnItems = getColumnItems(column.id);
                            return (
                                <KanbanColumn
                                    key={column.id}
                                    id={column.id}
                                    title={column.title}
                                    count={columnItems.length}
                                    headerStyle={column.headerStyle}
                                    items={columnItems}
                                    activeMenuId={activeMenuId}
                                    setActiveMenuId={setActiveMenuId}
                                    updateMissionStatus={updateMissionStatus}
                                    deleteMission={deleteMission}
                                    archiveMission={archiveMission}
                                    onCardClick={(item) => {
                                        navigate(`/missions/${item.id}`);
                                    }}
                                />
                            );
                        })}
                    </div>
                    <DragOverlay>
                        {activeDragItemId ? (
                            <div className="opacity-80 rotate-2 scale-105 cursor-grabbing pointer-events-none">
                                <SortableMissionCard
                                    mission={kanbanItems.find(i => i.id === activeDragItemId)!}
                                    activeMenuId={null}
                                    setActiveMenuId={() => { }}
                                    updateMissionStatus={() => { }}
                                    deleteMission={() => { }}
                                    archiveMission={() => { }}
                                    columnId="overlay"
                                    onClick={() => { }}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            </div>

            {/* ARCHIVED MISSIONS MODAL */}
            {showArchived && archivedCount > 0 && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div
                        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setShowArchived(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200 z-10">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                                <Archive size={18} className="text-slate-400" />
                                <h3 className="font-bold text-slate-800">Missions archivées</h3>
                                <span className="ml-1 bg-slate-100 text-slate-500 text-xs font-bold px-2 py-0.5 rounded-full">{archivedCount}</span>
                            </div>
                            <button
                                onClick={() => setShowArchived(false)}
                                className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {/* List */}
                        <div className="overflow-y-auto p-4 space-y-2 flex-1">
                            {[...kanbanItems.filter((i: KanbanItem) => isArchived(i.status))].reverse().map(mission => (
                                <div
                                    key={mission.id}
                                    className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                                    onClick={() => {
                                        setShowArchived(false);
                                        navigate(`/missions/${mission.id}`);
                                    }}
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-9 h-9 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center font-bold text-slate-500 text-xs">
                                            {mission.client.substring(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{mission.client}</div>
                                            <p className="text-sm font-semibold text-slate-700 truncate">{mission.title}</p>
                                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                                                <span className="flex items-center gap-1">
                                                    <MapPin size={11} />
                                                    {mission.location ? mission.location.split(',')[0].trim() : 'NC'}
                                                </span>
                                                {mission.lastUpdate && (
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={11} />
                                                        {(() => { try { return new Date(mission.lastUpdate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return mission.lastUpdate; } })()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); restoreMission(mission.id); }}
                                        className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                                    >
                                        <RefreshCw size={12} />
                                        Restaurer
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/*
        ============================================
        NEW MISSION MODAL
        ============================================
      */}
            {isNewMissionModalOpen && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setIsNewMissionModalOpen(false)}
                    ></div>

                    {/* Modal Container */}
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 z-10 flex flex-col max-h-[90vh]">
                        {/* Header */}
                        <div className="px-6 py-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                            <h3 className="font-bold text-xl text-slate-900">Nouvelle Mission</h3>
                            <button
                                onClick={() => setIsNewMissionModalOpen(false)}
                                className="p-1.5 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-8 overflow-y-auto">

                            {/* Option A: Dropzone */}
                            <div className="mb-6">
                                <FileDropZone
                                    label=""
                                    subLabel="Glissez votre AO ici (PDF, DOCX) ou cliquez pour parcourir"
                                    accept=".pdf,.doc,.docx"
                                    files={newMissionFiles}
                                    onFilesChange={setNewMissionFiles}
                                    icon={<FileText size={32} />}
                                />
                            </div>

                            {/* Divider */}
                            <div className="flex items-center gap-4 mb-6">
                                <div className="h-px bg-slate-200 flex-1"></div>
                                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">- OU -</span>
                                <div className="h-px bg-slate-200 flex-1"></div>
                            </div>

                            {/* Option B: Text Area */}
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">
                                    Coller le texte de la mission
                                </label>
                                <textarea
                                    className="w-full p-4 rounded-xl text-sm min-h-[120px] resize-y transition-all text-orange-600 placeholder-gray-400 bg-white border border-gray-300 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500 caret-orange-600"
                                    placeholder="Ex: Recherche Développeur Fullstack React/Node.js pour une mission de 6 mois..."
                                    value={newMissionText}
                                    onChange={(e) => setNewMissionText(e.target.value)}
                                />
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="p-6 bg-slate-50 border-t border-slate-200">
                            <button
                                onClick={handleCreateMission}
                                disabled={newMissionFiles.length === 0 && newMissionText.trim().length === 0}
                                className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all transform hover:-translate-y-0.5 ${(newMissionFiles.length === 0 && newMissionText.trim().length === 0) ? 'bg-slate-300 cursor-not-allowed shadow-none' : 'bg-amber-600 hover:bg-amber-700 hover:shadow-amber-200'}`}
                            >
                                <Sparkles size={18} />
                                Analyser & Créer
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MissionRegistryView;
